import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Loader2 } from "lucide-react";
import { API } from "@/App";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export default function AuthDialog({ open, onClose }) {
  const { loginWithPassword, register } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleLogin = () => {
    setGoogleLoading(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const switchMode = (next) => {
    setMode(next);
    setError("");
    setInfo("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      if (mode === "login") {
        await loginWithPassword(email, password);
        onClose();
      } else if (mode === "register") {
        await register(name, email, password);
        onClose();
      } else {
        const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
        setInfo(data.message);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const titles = { login: "SIGN IN", register: "CREATE ACCOUNT", forgot: "RESET PASSWORD" };
  const inputCls = "bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#00E0FF]/50";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-solid max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white text-2xl text-center" style={{ fontFamily: 'Unbounded' }}>
            {titles[mode]}
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-center">
            {mode === "forgot"
              ? "Enter your email and we'll send you a reset link"
              : "Access AI predictions, portfolio tracking, and price alerts"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {mode !== "forgot" && (
            <>
              <Button
                data-testid="google-login-btn"
                onClick={handleGoogleLogin}
                disabled={googleLoading}
                className="w-full bg-white text-black hover:bg-gray-100 font-semibold py-5 flex items-center justify-center gap-3"
              >
                <GoogleIcon />
                {googleLoading ? "Redirecting..." : "Continue with Google"}
              </Button>

              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-white/10" />
                <span className="text-xs text-gray-500 uppercase tracking-wider">or with email</span>
                <div className="h-px flex-1 bg-white/10" />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {mode === "register" && (
              <Input
                data-testid="auth-name-input"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className={inputCls}
              />
            )}
            <Input
              data-testid="auth-email-input"
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={inputCls}
            />
            {mode !== "forgot" && (
              <Input
                data-testid="auth-password-input"
                type="password"
                placeholder={mode === "register" ? "Password (min 8 characters)" : "Password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className={inputCls}
              />
            )}

            {error && (
              <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>
            )}
            {info && (
              <p data-testid="auth-info" className="text-sm text-[#00E0FF]">{info}</p>
            )}

            <Button
              data-testid="auth-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-semibold py-5"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {mode === "login" ? "Sign In" : mode === "register" ? "Create Account" : "Send Reset Link"}
            </Button>
          </form>

          <div className="flex items-center justify-between text-sm">
            {mode === "login" && (
              <>
                <button data-testid="auth-switch-register" type="button" onClick={() => switchMode("register")} className="text-gray-400 hover:text-white transition-colors">
                  New here? <span className="text-[#00E0FF]">Create account</span>
                </button>
                <button data-testid="auth-switch-forgot" type="button" onClick={() => switchMode("forgot")} className="text-gray-400 hover:text-white transition-colors">
                  Forgot password?
                </button>
              </>
            )}
            {mode !== "login" && (
              <button data-testid="auth-switch-login" type="button" onClick={() => switchMode("login")} className="text-gray-400 hover:text-white transition-colors">
                Already have an account? <span className="text-[#00E0FF]">Sign in</span>
              </button>
            )}
          </div>

          {mode === "register" && (
            <div className="glass-effect p-3 rounded-lg border border-[#00E0FF]/20">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-[#00E0FF] mt-0.5" />
                <div className="text-sm text-gray-400">
                  <span className="text-white font-semibold">Free Trial</span> — Get 3 free AI predictions when you sign up!
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
